import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Seo from "@/components/feature/Seo";
import { rankArticles, editorPicks, articles, type ArticleMeta } from "@/mocks/articles";
import { categories } from "@/mocks/site";

function RankRow({ article, index }: { article: ArticleMeta; index: number }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex items-center gap-4 rounded-xl bg-background-50 p-3 transition-all hover:-translate-y-0.5 hover:border hover:border-primary-200 md:p-4"
    >
      <span className="w-8 shrink-0 text-center font-heading text-2xl font-black text-primary-500 md:text-3xl">
        {index + 1}
      </span>
      <div className="h-16 w-24 shrink-0 overflow-hidden rounded-lg bg-background-200 md:h-20 md:w-32">
        <img
          src={article.cover}
          alt={article.coverAlt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="truncate font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
          {article.title}
        </h3>
        <p className="mt-1 hidden text-sm text-foreground-500 md:line-clamp-1">{article.excerpt}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-foreground-400">
          <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-900">{article.categoryName}</span>
          <span>{article.author}</span>
          <span>·</span>
          <span>{article.date}</span>
        </div>
      </div>
      <i className="ri-arrow-right-line hidden shrink-0 text-foreground-300 transition-colors group-hover:text-primary-600 sm:block" />
    </Link>
  );
}

export default function RankPage() {
  const [tab, setTab] = useState<"hot" | "pick">("hot");
  const list = tab === "hot" ? rankArticles : editorPicks;
  const sectionName = tab === "hot" ? "热门" : "编辑精选";

  return (
    <PageLayout dark={false}>
      <Seo
        title={`热门榜单 - 看料${sectionName}推荐，每日不错过好料`}
        description="看料热门榜单与编辑精选榜单，由编辑团队每日刷新，覆盖数字工作、阅读新知、生活灵感与认知成长等主题，帮你快速找到当下最值得看的内容。"
        keywords="看料,热门榜单,看料热门推荐,编辑精选,看料推荐,每日好料"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "看料热门榜单",
            description: "编辑团队每日甄选的热门与精选好料榜单。",
            url: "https://01x0wyo.cn/rank",
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://01x0wyo.cn/" },
              { "@type": "ListItem", position: 2, name: "热门榜单", item: "https://01x0wyo.cn/rank" },
            ],
          },
        ]}
      />

      {/* 头 */}
      <section className="bg-background-100 border-b border-background-200/70 pt-24 md:pt-28">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">热门榜单</span>
          </nav>
          <div className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-900">
                <i className="ri-trophy-line" />
                编辑每日刷新
              </span>
              <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-5xl">
                看料 · 每日榜单
              </h1>
              <p className="mt-4 text-base leading-8 text-foreground-600">
                热门榜反映当下最受关注的内容，编辑精选榜则由主编优中选优。
                一键切换，找到今天真正值得花时间的好料。
              </p>
            </div>

            {/* 榜类型切换 */}
            <div className="inline-flex shrink-0 self-start rounded-full bg-background-200/70 p-1" role="tablist">
              {(["hot", "pick"] as const).map((key) => {
                const on = tab === key;
                return (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={on}
                    type="button"
                    onClick={() => setTab(key)}
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                      on ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-700"
                    }`}
                  >
                    <i className={key === "hot" ? "ri-fire-fill" : "ri-star-fill"} />
                    {key === "hot" ? "热门榜" : "编辑精选榜"}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 栏目快捷 */}
          <div className="mt-8 flex flex-wrap gap-2">
            <Link to="/" className="rounded-full border border-background-300/70 bg-background-50 px-3.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap">
              全站好料
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="rounded-full border border-background-300/70 bg-background-50 px-3.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 榜单列表 */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-4xl px-4 py-10 md:px-6 md:py-14">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950">
              {tab === "hot" ? "今日热门好料" : "本周编辑力荐"}
            </h2>
            <span className="text-xs text-foreground-500">共 {list.length} 篇 · 排名按热度排列</span>
          </div>
          <div className="mt-6 space-y-3">
            {list.map((a, i) => (
              <RankRow key={a.slug} article={a} index={i} />
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-background-200/80 bg-background-100 p-6 text-center md:p-8">
            <p className="font-heading text-lg font-bold text-foreground-950">
              还想看更多？{sectionName}榜每日刷新
            </p>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-7 text-foreground-600">
              榜单之外，看料还有 {articles.length} 篇精选好料等你发现。按兴趣选择栏目，慢慢逛，别错过值得看的。
            </p>
            <Link
              to="/"
              className="mt-5 inline-flex h-11 items-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              返回首页继续逛 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}